import { useLocation, useSearchParams } from 'react-router-dom';
import { getStoredBranchId } from '../branch-settings/branchSettingsState.js';
import { currentMerchantScope } from '../../shell/merchantContext.js';
import {
  CATEGORY_ID_STORAGE_KEY,
  MODIFIER_GROUP_ID_STORAGE_KEY,
  PRODUCT_ID_STORAGE_KEY,
} from './catalog.js';

/** `new=1` means create only on the product-editor route, so overlays can keep productId. */
export function productIdFromSearch(pathname: string, params: URLSearchParams): string | null {
  if (pathname === '/product-editor' && params.get('new') === '1') return null;
  return params.get('productId') ?? params.get('id');
}

export function useProductIdParam(): string | null {
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  return productIdFromSearch(pathname, params);
}

export function useCategoryIdParam(): string | null {
  const [params] = useSearchParams();
  if (params.get('new') === '1') return null;
  return params.get('categoryId') ?? params.get('id');
}

export function useModifierGroupIdParam(): string | null {
  const [params] = useSearchParams();
  if (params.get('new') === '1') return null;
  return params.get('modifierGroupId') ?? params.get('groupId');
}

export function useBranchIdParam(): string | null {
  const [params] = useSearchParams();
  return params.get('branchId') ?? getStoredBranchId() ?? currentMerchantScope().branchId;
}

export function getStoredProductId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(PRODUCT_ID_STORAGE_KEY);
}

export function setStoredProductId(productId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(PRODUCT_ID_STORAGE_KEY, productId);
}

export function clearStoredProductId(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(PRODUCT_ID_STORAGE_KEY);
}

export function getStoredCategoryId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(CATEGORY_ID_STORAGE_KEY);
}

export function setStoredCategoryId(categoryId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(CATEGORY_ID_STORAGE_KEY, categoryId);
}

export function getStoredModifierGroupId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  return localStorage.getItem(MODIFIER_GROUP_ID_STORAGE_KEY);
}

export function setStoredModifierGroupId(groupId: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(MODIFIER_GROUP_ID_STORAGE_KEY, groupId);
}

export function catalogueOverviewPath(): string {
  return '/catalogue-overview';
}

export function categoryListPath(): string {
  return '/category-list';
}

export function categoryEditorPath(categoryId?: string): string {
  return categoryId
    ? `/category-editor?categoryId=${encodeURIComponent(categoryId)}`
    : '/category-editor?new=1';
}

export function productListPath(opts?: { categoryId?: string }): string {
  if (opts?.categoryId) {
    return `/product-list?categoryId=${encodeURIComponent(opts.categoryId)}`;
  }
  return '/product-list';
}

export function productEditorPath(productId?: string, opts?: { step?: 1 | 2 | 3 }): string {
  if (!productId) return '/product-editor?new=1';
  const params = new URLSearchParams({ productId });
  if (opts?.step === 2 || opts?.step === 3) params.set('step', String(opts.step));
  return `/product-editor?${params.toString()}`;
}

/** Operational editor journey: 1 Details, 2 Photo & allergens, 3 Extras. */
export function productEditorJourneyStep(params: URLSearchParams): 1 | 2 | 3 {
  const step = Number(params.get('step'));
  return step === 2 || step === 3 ? step : 1;
}

/** 07-07 save-and-add-another retains category and tax on the next create. */
export function productEditorCreatePath(retain?: {
  categoryId?: string | null;
  taxCode?: string | null;
}): string {
  const params = new URLSearchParams({ new: '1' });
  if (retain?.categoryId) params.set('categoryId', retain.categoryId);
  if (retain?.taxCode) params.set('taxCode', retain.taxCode);
  return `/product-editor?${params.toString()}`;
}

export function variantEditorPath(productId: string): string {
  return `/variant-editor?productId=${encodeURIComponent(productId)}`;
}

export function modifierGroupListPath(): string {
  return '/modifier-group-list';
}

export function productIdFromReturnTo(returnTo?: string | null): string | null {
  if (!returnTo?.startsWith('/')) return null;
  try {
    return new URL(returnTo, 'https://beatq.local').searchParams.get('productId');
  } catch {
    return null;
  }
}

export function modifierGroupEditorPath(groupId?: string, opts?: { returnTo?: string }): string {
  const params = new URLSearchParams();
  if (groupId) params.set('modifierGroupId', groupId);
  else params.set('new', '1');
  if (opts?.returnTo) params.set('returnTo', opts.returnTo);
  const productId = productIdFromReturnTo(opts?.returnTo);
  if (productId) params.set('productId', productId);
  return `/modifier-group-editor?${params.toString()}`;
}

/** Close target for the 026 modal. History entry so Back closes (07-03). */
export function modifierGroupEditorClosePath(returnTo?: string | null): string {
  return returnTo?.startsWith('/') ? returnTo : modifierGroupListPath();
}

export function modifierGroupEditorOverlaysProduct(returnTo?: string | null): boolean {
  return modifierGroupEditorClosePath(returnTo).startsWith('/product-editor');
}

/** 07-05 §18: confirm before leaving a dirty product editor. */
export function productEditorHasUnsavedChanges(input: {
  isCreate: boolean;
  name: string;
  description: string;
  categoryId: string;
  pricePounds: string;
  taxCode: string;
  savedName?: string | null;
  savedDescription?: string | null;
  savedCategoryId?: string | null;
  savedPriceMinor?: number | null;
  savedTaxCode?: string | null;
}): boolean {
  if (input.isCreate) {
    return Boolean(
      input.name.trim() ||
      input.description.trim() ||
      input.categoryId.trim() ||
      input.pricePounds.trim(),
    );
  }
  const liveMinor = input.pricePounds.trim()
    ? Math.round(Number.parseFloat(input.pricePounds) * 100)
    : null;
  const savedMinor = input.savedPriceMinor ?? null;
  const priceDirty =
    input.pricePounds.trim() !== '' &&
    liveMinor != null &&
    Number.isFinite(liveMinor) &&
    liveMinor !== savedMinor;
  return (
    input.name.trim() !== (input.savedName ?? '').trim() ||
    input.description.trim() !== (input.savedDescription ?? '').trim() ||
    input.categoryId !== (input.savedCategoryId ?? '') ||
    priceDirty ||
    input.taxCode !== (input.savedTaxCode ?? 'STANDARD')
  );
}

export function allergenEditorPath(productId: string): string {
  return `/allergen-editor?productId=${encodeURIComponent(productId)}`;
}

export function restrictedProductSettingsPath(productId: string): string {
  return `/restricted-product-settings?productId=${encodeURIComponent(productId)}`;
}

export function bulkImportPath(): string {
  return '/bulk-import';
}

export function branchOverridesPath(productId: string, branchId?: string): string {
  const params = new URLSearchParams({ productId });
  if (branchId) params.set('branchId', branchId);
  return `/branch-overrides?${params.toString()}`;
}

/** Catalogue IA landing for SCR-MER-030 when no product is chosen yet. */
export function branchOverridesLandingPath(): string {
  return '/branch-overrides';
}

export function resolveProductCategoryName(
  product: {
    categoryName?: string | null;
    categoryId?: string | null;
    categoryIds?: string[];
  },
  categories: Array<{ id?: string; name?: string }>,
): string {
  if (product.categoryName?.trim()) return product.categoryName.trim();
  const ids = [...(product.categoryId ? [product.categoryId] : []), ...(product.categoryIds ?? [])];
  const match = categories.find((category) => category.id && ids.includes(category.id));
  return match?.name?.trim() || '—';
}

export function resolveCategoryProductCount(
  category: { id?: string; productCount?: number | null },
  products: Array<{ categoryId?: string | null; categoryIds?: string[] }>,
): number {
  if (typeof category.productCount === 'number') return category.productCount;
  if (!category.id) return 0;
  return products.filter(
    (product) => product.categoryId === category.id || product.categoryIds?.includes(category.id!),
  ).length;
}

export function confirmCatalogueDelete(
  message: string,
  confirmFn: (prompt: string) => boolean = (prompt) =>
    typeof window !== 'undefined' ? window.confirm(prompt) : false,
): boolean {
  return confirmFn(message);
}

export function catalogueInUseGuardCopy(kind: 'category' | 'modifier', count: number): string {
  const plural = count === 1 ? '' : 's';
  if (kind === 'category') {
    return `Move or remove the ${count} product${plural} in this category first.`;
  }
  return `This group is used on ${count} product${plural}. Detach it first.`;
}

const SEED_CATEGORY_NAMES = new Set(['burgers', 'sides', 'drinks']);

export function isSeedOperatingCategory(name?: string | null): boolean {
  return SEED_CATEGORY_NAMES.has((name ?? '').trim().toLowerCase());
}

/** Prior audit/probe sessions left named leftovers. Seed menu names stay operating. */
export function isLeftoverAuditCategory(name?: string | null): boolean {
  const value = (name ?? '').trim();
  if (!value || isSeedOperatingCategory(value)) return false;
  return /^(mer04|verify cat|journey cat|debug-cat|ncat|hiddenlie|test category|gap cat|qa compact|az bowl|hours \d+)|probe/i.test(
    value,
  );
}

export function leftoverCategoriesBannerCopy(input: {
  leftoverCount: number;
  hiddenCount: number;
}): string {
  const leftover =
    input.leftoverCount === 1
      ? '1 leftover category sits off your operating list. Tidy unused leftovers to hide it from customers and delete it if it is empty.'
      : input.leftoverCount > 0
        ? `${input.leftoverCount} leftover categories sit off your operating list. Tidy unused leftovers to hide them from customers and delete empty ones.`
        : '';
  const hidden =
    input.hiddenCount === 1
      ? '1 category is hidden from the customer menu.'
      : input.hiddenCount > 0
        ? `${input.hiddenCount} categories are hidden from the customer menu.`
        : '';
  return [leftover, hidden, 'Burgers, Sides and Drinks stay on the menu.']
    .filter(Boolean)
    .join(' ');
}

export function categoryVisibilityLabel(category: {
  hidden?: boolean;
  isVisible?: boolean;
  productCount?: number | null;
}): string {
  const isHidden = category.hidden === true || category.isVisible === false;
  if (isHidden) return 'Hidden';
  if ((category.productCount ?? 0) === 0) return 'Empty';
  return 'Visible';
}

export function categoriesForProductPicker<
  T extends { name?: string; hidden?: boolean; isVisible?: boolean },
>(categories: T[]): T[] {
  const { operating, leftover, hidden } = partitionMerchantCategories(categories);
  return [...operating, ...hidden, ...leftover];
}

export function categoryEditorHasUnsavedChanges(input: {
  name: string;
  description: string;
  availableFrom: string;
  availableTo: string;
  mediaAssetId: string;
  hidden: boolean;
  savedName: string;
  savedDescription: string;
  savedAvailableFrom: string;
  savedAvailableTo: string;
  savedMediaAssetId: string;
  savedHidden: boolean;
}): boolean {
  return (
    input.name.trim() !== input.savedName.trim() ||
    input.description.trim() !== input.savedDescription.trim() ||
    input.availableFrom.trim() !== input.savedAvailableFrom.trim() ||
    input.availableTo.trim() !== input.savedAvailableTo.trim() ||
    input.mediaAssetId.trim() !== input.savedMediaAssetId.trim() ||
    input.hidden !== input.savedHidden
  );
}

export function partitionMerchantCategories<
  T extends { name?: string; hidden?: boolean; isVisible?: boolean },
>(categories: T[]): { operating: T[]; leftover: T[]; hidden: T[] } {
  const leftover: T[] = [];
  const hidden: T[] = [];
  const operating: T[] = [];
  for (const category of categories) {
    const isHidden = category.hidden === true || category.isVisible === false;
    if (isLeftoverAuditCategory(category.name)) leftover.push(category);
    else if (isHidden) hidden.push(category);
    else operating.push(category);
  }
  return { operating, leftover, hidden };
}

export function leftoverCategoryCleanupPlan<
  T extends {
    id?: string;
    name?: string;
    productCount?: number | null;
    hidden?: boolean;
    isVisible?: boolean;
  },
>(categories: T[]): { removable: T[]; hidable: T[] } {
  const leftover = categories.filter(
    (category) => isLeftoverAuditCategory(category.name) && category.id,
  );
  return {
    removable: leftover.filter((category) => (category.productCount ?? 0) === 0),
    hidable: leftover.filter((category) => {
      const alreadyHidden = category.hidden === true || category.isVisible === false;
      return (category.productCount ?? 0) > 0 && !alreadyHidden;
    }),
  };
}

export function nestedEditorBackLabel(returnTo?: string | null): string {
  return modifierGroupEditorOverlaysProduct(returnTo) ? 'Back to product' : 'Close';
}

/** SCR-MER-019 overview: fields already on the product list payload. */
export type OverviewProductLike = {
  publicationState?: string;
  status?: string;
  allergenDeclarationComplete?: boolean;
  imageUrl?: string | null;
  mediaAssetId?: string | null;
  images?: Array<{ mediaAssetId?: string | null; imageUrl?: string | null }>;
};

export function productPublicationState(product: OverviewProductLike): string {
  return product.publicationState ?? product.status ?? 'DRAFT';
}

export function productIsPublished(product: OverviewProductLike): boolean {
  return productPublicationState(product) === 'PUBLISHED';
}

export function productPhotoFieldsPresent(product: OverviewProductLike): boolean {
  return (
    product.imageUrl !== undefined ||
    product.mediaAssetId !== undefined ||
    product.images !== undefined
  );
}

export function productHasCustomerPhoto(product: OverviewProductLike): boolean {
  if (typeof product.imageUrl === 'string' && product.imageUrl.trim()) return true;
  if (typeof product.mediaAssetId === 'string' && product.mediaAssetId.trim()) return true;
  return (product.images ?? []).some(
    (image) =>
      (typeof image.mediaAssetId === 'string' && image.mediaAssetId.trim()) ||
      (typeof image.imageUrl === 'string' && image.imageUrl.trim()),
  );
}

export function overviewNeedsAttention(product: OverviewProductLike): boolean {
  if (product.allergenDeclarationComplete === false) return true;
  if (!productIsPublished(product)) return true;
  return productPhotoFieldsPresent(product) && !productHasCustomerPhoto(product);
}

/** One plain-English reason; most blocking first. Null when the item is healthy. */
export function overviewAttentionIssue(product: OverviewProductLike): string | null {
  if (product.allergenDeclarationComplete === false) return 'Allergens not declared';
  if (!productIsPublished(product)) {
    return productPublicationState(product) === 'DRAFT'
      ? 'Draft — not on the customer menu yet'
      : 'Not on the customer menu yet';
  }
  if (productPhotoFieldsPresent(product) && !productHasCustomerPhoto(product)) {
    return 'Needs a photo';
  }
  return null;
}

export function overviewCanPublishFromList(product: OverviewProductLike): boolean {
  return (
    productPublicationState(product) === 'DRAFT' && product.allergenDeclarationComplete !== false
  );
}

export const PRODUCT_LIST_PAGE_SIZE = 100;

export function productListPageCount(total: number, pageSize = PRODUCT_LIST_PAGE_SIZE): number {
  return Math.max(1, Math.ceil(Math.max(0, total) / Math.max(1, pageSize)));
}

export function productListRange(
  page: number,
  pageSize: number,
  total: number,
): {
  page: number;
  from: number;
  to: number;
  pageCount: number;
  canPrev: boolean;
  canNext: boolean;
} {
  if (total <= 0) {
    return { page: 1, from: 0, to: 0, pageCount: 1, canPrev: false, canNext: false };
  }
  const pageCount = productListPageCount(total, pageSize);
  const safePage = Math.min(Math.max(1, page), pageCount);
  return {
    page: safePage,
    from: (safePage - 1) * pageSize + 1,
    to: Math.min(total, safePage * pageSize),
    pageCount,
    canPrev: safePage > 1,
    canNext: safePage < pageCount,
  };
}
